import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useUI } from '@/store/ui'
import { AuthAPI } from '@/api/auth'
import { ApiError } from '@/api/client'
import { LandingAtmosphere } from '@/components/LandingAtmosphere'
import { DiwaSigil } from '@/components/DiwaSigil'
import { callbackRedirectUri, consumeState } from '@/lib/gateSso'
import { postAuthDestination } from '@/lib/inviteIntent'
import './landing.css'

// The registered Gate callback (one_login_spec.md §4 step 3/4): the Gate lands
// the browser here with ?code=&state=. We validate the parked state, hand the
// one-time code to the diwa backend (which redeems it server-side via Warden),
// and step into a perfectly ordinary diwa session. The code never goes anywhere
// except the backend; no token ever rides the URL.
export function GateCallback() {
  const { diwaUrl, setToken, pendingInvite } = useUI()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [error, setError] = useState<string | null>(null)
  // The code is single-use, so the exchange must fire exactly once — even under
  // StrictMode's dev double-mount.
  const fired = useRef(false)

  useEffect(() => {
    if (fired.current) return
    fired.current = true

    const code = params.get('code')
    const { ok, from } = consumeState(sessionStorage, params.get('state'))
    if (!code || !ok) {
      setError('This sign-in link is stale or was already used — head back and try again.')
      return
    }
    const api = new AuthAPI(diwaUrl)
    api
      .gateExchange(code, callbackRedirectUri(window.location.origin))
      .then(({ access_token }) => {
        setToken(access_token)
        navigate(postAuthDestination({ pendingInvite, from }), { replace: true })
      })
      .catch((err) => {
        setError(
          err instanceof ApiError && err.detail
            ? err.detail
            : 'Your Diwata ID sign-in could not be completed — try again.',
        )
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="dwl">
      <LandingAtmosphere />
      <main className="dwl-login">
        <div className="dwl-login-card">
          <DiwaSigil size={60} className="dwl-login-sigil" />
          <h1 className="dwl-login-title">Diwa Domains</h1>
          {error ? (
            <>
              <p className="dwl-login-error" role="alert">
                {error}
              </p>
              <Link to="/login" className="dwl-btn dwl-login-submit" style={{ textAlign: 'center' }}>
                Back to sign in
              </Link>
            </>
          ) : (
            <p className="dwl-login-sub" role="status">
              <span className="dwl-login-spinner" aria-hidden="true" /> Finishing your Diwata ID
              sign-in…
            </p>
          )}
        </div>
      </main>
    </div>
  )
}
